import { createSlice } from "@reduxjs/toolkit";
import { updateUnits } from "@Services/utils/helpers";
import _ from "lodash";

const hoopsModalSlice = createSlice({
  name: "HoopsModalStatus",
  initialState: [],
  reducers: {
    setShowOnlyModal: (state, action) => {
      const keysToShow = Array.isArray(action.payload) ? action.payload : [action.payload];

      Object.keys(state).forEach((key) => {
        if (!keysToShow.includes(key) && key !== "Viewer") {
          state[key].show = false;
        }
      });

      keysToShow.forEach((key) => {
        if (!Object.hasOwn(state, key)) {
          state[key] = {};
        }
        state[key].show = true;
      });
    },
    setModalStatus: (_state, action) => action.payload,
    setAnimationForIssueCreation: (state, action) => ({
      ...state,
      Issues: {
        ...state.Issues,
        flagForAnimation: action.payload,
      },
    }),
  },
});

const sheetActiveSlice = createSlice({
  name: "isSheetActive",
  initialState: false,
  reducers: {
    setIsSheetActive: (_state, action) => action.payload,
  },
});

const faceSelectionSlice = createSlice({
  name: "faceSelection",
  initialState: false,
  reducers: {
    setFaceSelection: (_state, action) => action.payload,
  },
});

const defaultModelProperties = {
  data: [],
  isLoading: false,
  isLoaded: false,
  isError: false,
};

const modelPropertiesSlice = createSlice({
  name: "modelProperties",
  initialState: defaultModelProperties,
  reducers: {
    setModelProperties: (state, action) => ({ ...state, ...action.payload }),
    modifyMetadataUnits: (state, action) => {
      const { fileVersionId, unitsNew, portalUnits } = action.payload;
      const filesProperties = state.data?.[fileVersionId];
      if (!filesProperties) return state;
      const updatedDataByNodeId = Object.entries(filesProperties).reduce((acc, [nodeId, node]) => {
        const updatedProperties = updateUnits(node.properties, unitsNew, portalUnits);
        const updatedTypeProperties = updateUnits(node.typeProperties, unitsNew, portalUnits);
        acc[nodeId] = {
          ...node,
          properties: updatedProperties,
          typeProperties: updatedTypeProperties,
        };
        return acc;
      }, {});
      return {
        ...state,
        data: {
          ...state.data,
          [fileVersionId]: updatedDataByNodeId,
        },
      };
    },
    updateCustomMetadataInModelProperties: (state, action) => {
      const { fileVersionId, nodeId, fieldsData, selectedFormTemplate } = action.payload;
      //Temp for demo
      let updatedData = [];
      if (Object.values(state.data[fileVersionId]).length <= 0) {
        const updatedCustomProperties = {};
        if (Array.isArray(fieldsData.value) && fieldsData.value.length === 0) {
          delete updatedCustomProperties[fieldsData._id];
        } else {
          updatedCustomProperties[fieldsData._id] = fieldsData.value;
        }
        nodeId.map((node) => {
          updatedData.push({
            fileVersionId,
            nodeId: node,
            selectedFormTemplate: { _id: selectedFormTemplate },
            customProperties: updatedCustomProperties,
          });
        });
      } else {
        updatedData = Object.values(state.data[fileVersionId]).map((item) => {
          if (item.fileVersionId === fileVersionId && nodeId.includes(item.nodeId)) {
            const updatedCustomProperties = { ...item.customProperties };
            if (Array.isArray(fieldsData.value) && fieldsData.value.length === 0) {
              delete updatedCustomProperties[fieldsData._id];
            } else {
              updatedCustomProperties[fieldsData._id] = fieldsData.value;
            }
            return {
              ...item,
              selectedFormTemplate: { _id: selectedFormTemplate },
              customProperties: updatedCustomProperties,
            };
          }
          return item;
        });
      }
      const updatedDataByNodeId = updatedData.reduce((acc, item) => {
        acc[item.nodeId] = item;
        return acc;
      }, {});
      return {
        ...state,
        data: {
          ...state.data,
          [fileVersionId]: updatedDataByNodeId,
        },
      };
    },
    updateAssetDataInModelProperties: (state, reduxAction) => {
      const { property, data, action } = reduxAction.payload;
      Object.keys(data).forEach((fileVersionId) => {
        data[fileVersionId].forEach((nodeId) => {
          const nodeData = _.get(state, ["data", fileVersionId, nodeId]);
          if (nodeData) {
            if (!Array.isArray(nodeData.assetData)) {
              nodeData.assetData = [];
            }
            if (action === "add") {
              if (!nodeData.assetData.includes(property)) {
                nodeData.assetData.push(property);
              }
            } else if (action === "remove") {
              nodeData.assetData = nodeData.assetData.filter((item) => item != property);
            }
          }
        });
      });
    },
  },
});

const settingModalSlice = createSlice({
  name: "GetSettingModalData",
  initialState: {},
  reducers: {
    setSettingModalAPIData: (_state, action) => action.payload,
    updateSettingModalAPIData: (state, action) => ({ ...state, ...action.payload }),
  },
});

const nodeIdAndUniqueIdMapSlice = createSlice({
  name: "nodeIdAndUniqueIdMap",
  initialState: {},
  reducers: {
    setNodeIdAndUniqueIdMap: (state, action) => {
      const { fileVersionId, data } = action.payload;
      const cubeUniqueIdMap = {};
      for (const i in data) {
        if (Object.hasOwn(cubeUniqueIdMap, data[i])) {
          cubeUniqueIdMap[data[i]].push(Number(i));
        } else {
          cubeUniqueIdMap[data[i]] = [Number(i)];
        }
      }
      return {
        ...state,
        [fileVersionId]: {
          nodeIdMap: data,
          cubeUniqueIdMap,
        },
      };
    },
  },
});

const bottomToolbarSlice = createSlice({
  name: "isBottomToolbarRefreshRequired",
  initialState: false,
  reducers: {
    setBottomToolbarRefresh: (_state, action) => action.payload,
  },
});

const transparentElementsSlice = createSlice({
  name: "transparentElements",
  initialState: [],
  reducers: {
    updateTransparentElements: (_state, action) => action.payload,
  },
});

const DEFAULT_VIEWER_STATE = {
  isSidebarOpen: true,
  markupAnnotations: [],
  nonMarkupAnnotations: [],
  currentPageInfo: {
    pageNumber: 1,
    reason: null,
    zoomLevel: 1,
    xScrollPosition: 0,
    yScrollPosition: 0,
  },
  showAllIssueMarkups: false,
  reason: "reset_viewer_state",
  showObjectTreeShimmer: true,
};

const viewerStateSlice = createSlice({
  name: "viewerState",
  initialState: DEFAULT_VIEWER_STATE,
  reducers: {
    resetViewerState: (state) => ({ ...state, ...DEFAULT_VIEWER_STATE }),
    setViewerState: (state, action) => {
      return {
        ...state,
        ...action.payload,
        reason: action.payload.reason,
      };
    },
    updateViewerState: (state, action) => ({
      ...state,
      [action.payload.field]: action.payload.value,
      reason: action.payload.reason,
    }),
    setAnnotations: (state, action) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      return {
        ...state,
        [annotationType]: action.payload.data,
        reason: action.payload.reason,
      };
    },
    deleteAnnotation: (state, action) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      return {
        ...state,
        [annotationType]: state[annotationType].filter((annotation) => annotation.id !== action.payload.data),
        reason: action.payload.reason,
      };
    },
    addAnnotation: (state, action) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const isArray = Array.isArray(action.payload.data);
      const dataToAdd = isArray ? action.payload.data : [action.payload.data];
      return {
        ...state,
        [annotationType]: [...state[annotationType], ...dataToAdd],
        reason: action.payload.reason,
      };
    },
    updateAnnotationProperties: (state, action) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const { id, properties } = action.payload.data;
      const targetIndex = state[annotationType].findIndex((annotation) => annotation.id === id);
      if (targetIndex === -1) return state;
      const targetAnnotation = state[annotationType][targetIndex];
      const hasChanges = Object.entries(properties).some(([key, value]) => {
        const isEqual = _.isEqual(targetAnnotation[key], value);
        return !isEqual;
      });
      if (!hasChanges) {
        return {
          ...state,
          reason: "unchanged_annotations",
        };
      }
      const newAnnotations = [...state[annotationType]];
      newAnnotations[targetIndex] = { ...newAnnotations[targetIndex], ...properties };
      return {
        ...state,
        [annotationType]: newAnnotations,
        reason: action.payload.reason,
      };
    },
    setCurrentPagePDF: (state, action) => ({
      ...state,
      currentPageInfo: action.payload,
    }),
    setShowObjectTreeShimmer: (state, action) => ({
      ...state,
      showObjectTreeShimmer: action.payload,
    }),
  },
});

const selectedNestedMeasurementsSlice = createSlice({
  name: "getSelectedNestedMeasurements",
  initialState: {},
  reducers: {
    setSelectedNestedMeasurements: (_state, action) => action.payload,
  },
});

const measurementAttributePopupSlice = createSlice({
  name: "measurementAttributePopup",
  initialState: { show: false, callback: null, structure: null },
  reducers: {
    setMeasurementAttributePopup: (_state, action) => ({ ...action.payload }),
  },
});

const viewSlice = createSlice({
  name: "viewReducer",
  initialState: null,
  reducers: {
    setDefaultView: (_state, action) => action.payload,
  },
});

export const { setModalStatus, setShowOnlyModal, setAnimationForIssueCreation } = hoopsModalSlice.actions;
export const { setIsSheetActive } = sheetActiveSlice.actions;
export const { setFaceSelection } = faceSelectionSlice.actions;
export const {
  setModelProperties,
  modifyMetadataUnits,
  updateCustomMetadataInModelProperties,
  updateAssetDataInModelProperties,
} = modelPropertiesSlice.actions;
export const { setSettingModalAPIData, updateSettingModalAPIData } = settingModalSlice.actions;
export const { setNodeIdAndUniqueIdMap } = nodeIdAndUniqueIdMapSlice.actions;
export const { setBottomToolbarRefresh } = bottomToolbarSlice.actions;
export const { updateTransparentElements } = transparentElementsSlice.actions;
export const {
  resetViewerState,
  setViewerState,
  updateViewerState,
  setAnnotations,
  deleteAnnotation,
  addAnnotation,
  updateAnnotationProperties,
  setCurrentPagePDF,
  setShowObjectTreeShimmer,
} = viewerStateSlice.actions;
export const { setSelectedNestedMeasurements } = selectedNestedMeasurementsSlice.actions;
export const { setMeasurementAttributePopup } = measurementAttributePopupSlice.actions;
export const { setDefaultView } = viewSlice.actions;

export const HoopsModalStatus = hoopsModalSlice.reducer;
export const isSheetActive = sheetActiveSlice.reducer;
export const faceSelection = faceSelectionSlice.reducer;
export const modelProperties = modelPropertiesSlice.reducer;
export const GetSettingModalData = settingModalSlice.reducer;
export const nodeIdAndUniqueIdMap = nodeIdAndUniqueIdMapSlice.reducer;
export const isBottomToolbarRefreshRequired = bottomToolbarSlice.reducer;
export const transparentElements = transparentElementsSlice.reducer;
export const viewerState = viewerStateSlice.reducer;
export const getSelectedNestedMeasurements = selectedNestedMeasurementsSlice.reducer;
export const measurementAttributePopup = measurementAttributePopupSlice.reducer;
export const viewReducer = viewSlice.reducer;
